import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";
import type { ColumnVisibilityItem } from "providers/Auth";
import { COLUMNS_SAVE_ERROR } from "shared/hooks/use-column-visibility";
import {
  LEADS_COLUMNS_TAB_KEY,
  LEGACY_SESSION_STORAGE_COLUMNS_KEY,
  SESSION_STORAGE_VIEW_COLUMNS_KEY
} from "../constants";
import { mergeColumnsWithDefaults } from "../utils/columnVisibility";

const readViewColumns = (): ColumnVisibilityItem[] | null => {
  try {
    // The pre-CRM-1891 working columns no longer mean anything; drop them.
    sessionStorage.removeItem(LEGACY_SESSION_STORAGE_COLUMNS_KEY);
    const raw = sessionStorage.getItem(SESSION_STORAGE_VIEW_COLUMNS_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    // A corrupt blob with null or primitive entries would throw while merging.
    if (
      !Array.isArray(parsed) ||
      !parsed.every(
        (col) => col && typeof col === "object" && typeof col.id === "string"
      )
    )
      return null;
    return parsed as ColumnVisibilityItem[];
  } catch {
    return null;
  }
};

const sameColumns = (a: ColumnVisibilityItem[], b: ColumnVisibilityItem[]) =>
  a.length === b.length &&
  a.every((col, i) => col.id === b[i].id && col.checked === b[i].checked);

const storeViewColumns = (columns: ColumnVisibilityItem[] | null) => {
  try {
    if (columns)
      sessionStorage.setItem(
        SESSION_STORAGE_VIEW_COLUMNS_KEY,
        JSON.stringify(columns)
      );
    else sessionStorage.removeItem(SESSION_STORAGE_VIEW_COLUMNS_KEY);
  } catch {
    // Storage unavailable: after a reload the table shows the user's own columns.
  }
};

/**
 * The Leads table's columns (CRM-1891). Two sources:
 *   - the user's own columns, saved to their account under `columns_visibility.leads` and shared
 *     by the Leads page and the Leads tab of a Property, Unit and Contact;
 *   - while a saved view is applied, that view's columns, kept as working state (mirrored to
 *     sessionStorage on /leads so a reload keeps them) and saved only into the view itself.
 *
 * A user who never changed their columns has no `leads` tab saved and sees `defaults`.
 * Reset clears the saved tab, so they go back to `defaults` (CRM-1814 will put the account
 * default there).
 */
export const useLeadsColumns = ({
  defaults,
  persistParams
}: {
  defaults: ColumnVisibilityItem[];
  persistParams: boolean;
}) => {
  const { auth, refreshAuthUserDashboardState } = useAuthContext();
  const saved = auth?.columns_visibility?.[LEADS_COLUMNS_TAB_KEY];

  const accountColumns = useMemo(
    () => mergeColumnsWithDefaults(saved, defaults),
    [saved, defaults]
  );

  const [viewColumns, setViewColumns] = useState<ColumnVisibilityItem[] | null>(
    () => {
      const stored = persistParams ? readViewColumns() : null;
      return stored ? mergeColumnsWithDefaults(stored, defaults) : null;
    }
  );
  // What the user just set, shown until the saved copy comes back (or kept when saving failed).
  const [pendingAccountColumns, setPendingAccountColumns] = useState<
    ColumnVisibilityItem[] | null
  >(null);

  // Drop the local copy only once the saved columns caught up with it: when two drops overlap,
  // the first save's refresh must not snap the table back to the older order.
  useEffect(() => {
    setPendingAccountColumns((pending) =>
      pending && !sameColumns(pending, accountColumns) ? pending : null
    );
  }, [accountColumns]);

  // mutateAsync is stable across renders, unlike the mutation object, so callbacks stay memoized.
  const { mutateAsync: saveColumnsRequest } = useMutation({
    mutationKey: ["LEADS_COLUMN_VISIBILITY", "UPDATE"],
    mutationFn: (allTabs: Record<string, ColumnVisibilityItem[]>) =>
      instance.patch(`/portal/v1/users/${auth?.id}`, {
        columns_visibility: allTabs
      })
  });

  const saveAccountColumns = useCallback(
    async (next: ColumnVisibilityItem[], payloadColumns = next) => {
      setPendingAccountColumns(next);
      try {
        // Only the Leads tab: the API merges it into the stored tabs.
        await saveColumnsRequest({
          [LEADS_COLUMNS_TAB_KEY]: payloadColumns
        });
        refreshAuthUserDashboardState();
      } catch {
        toast.error(COLUMNS_SAVE_ERROR);
      }
    },
    [saveColumnsRequest, refreshAuthUserDashboardState]
  );

  const isViewApplied = viewColumns !== null;

  /** Change the columns the table shows (drag, show/hide). */
  const setColumns = useCallback(
    (next: ColumnVisibilityItem[]) => {
      if (isViewApplied) {
        setViewColumns(next);
        if (persistParams) storeViewColumns(next);
        return;
      }
      void saveAccountColumns(next);
    },
    [isViewApplied, persistParams, saveAccountColumns]
  );

  /** Show a saved view's columns, or (null) go back to the user's own columns. */
  const applyViewColumns = useCallback(
    (next: ColumnVisibilityItem[] | null) => {
      setViewColumns(next);
      if (persistParams) storeViewColumns(next);
    },
    [persistParams]
  );

  /** Reset to default: Boom's standard columns (the account default once CRM-1814 lands). */
  const reset = useCallback(() => {
    if (isViewApplied) {
      setViewColumns(defaults);
      if (persistParams) storeViewColumns(defaults);
      return;
    }
    // An empty list clears the saved tab, so the user counts as never having changed it.
    void saveAccountColumns(defaults, []);
  }, [isViewApplied, persistParams, defaults, saveAccountColumns]);

  return {
    columns: viewColumns ?? pendingAccountColumns ?? accountColumns,
    /** True while a saved view's columns are shown (until Default is picked). */
    isViewApplied,
    setColumns,
    applyViewColumns,
    reset
  };
};
